import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import MoveToInboxIcon from '@mui/icons-material/MoveToInboxOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import ChartCard from '@/components/dashboard/ChartCard';
import DashboardBarChart from '@/components/dashboard/DashboardBarChart';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { withId } from './reportHelpers';
import type { CurrentStockRow, DispatchOrderRegisterRow, GrnRegisterRow } from '@/types/reports';
import type { InvoiceDto } from '@/types/billing';

interface ClientStockSummaryRow {
  clientName: string;
  materialCount: number;
  quantityOnHand: number;
  quantityAllocated: number;
  quantityAvailable: number;
}

interface ClientActivitySummaryRow {
  clientName: string;
  grnCount: number;
  acceptedQty: number;
  dispatchCount: number;
  issuedQty: number;
}

interface ClientBillingSummaryRow {
  clientName: string;
  invoiceCount: number;
  totalInvoiced: number;
  paidAmount: number;
  unpaidAmount: number;
}

/** "Unassigned" bucket for rows with no clientId — shouldn't normally occur once 3PL is on and
 * every stock-affecting document enforces a Client, but a company that just switched 3PL on will
 * have pre-existing rows with none, and this keeps them visible rather than silently dropped. */
const UNASSIGNED = '—';

function StockSummaryTab() {
  const { t } = useTranslation('reports');
  const theme = useTheme();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['reports', 'current-stock', 'all-warehouses'],
    queryFn: async () => (await apiClient.get<CurrentStockRow[]>('/reports/current-stock')).data,
  });

  const summary = useMemo(() => {
    const byClient = new Map<string, ClientStockSummaryRow & { materials: Set<string> }>();
    for (const r of rows) {
      const key = r.clientName ?? UNASSIGNED;
      const entry = byClient.get(key) ?? { clientName: key, materialCount: 0, quantityOnHand: 0, quantityAllocated: 0, quantityAvailable: 0, materials: new Set<string>() };
      entry.materials.add(r.materialId);
      entry.quantityOnHand += r.quantityOnHand;
      entry.quantityAllocated += r.quantityAllocated;
      entry.quantityAvailable += r.quantityAvailable;
      byClient.set(key, entry);
    }
    return Array.from(byClient.values())
      .map((e) => ({ ...e, materialCount: e.materials.size }))
      .sort((a, b) => b.quantityOnHand - a.quantityOnHand);
  }, [rows]);

  const columns: DataTableColumn<ClientStockSummaryRow>[] = [
    { field: 'clientName', headerName: t('columns.client') },
    { field: 'materialCount', headerName: t('clientSummary.materialCount'), align: 'right' },
    { field: 'quantityOnHand', headerName: t('columns.onHand'), align: 'right' },
    { field: 'quantityAllocated', headerName: t('columns.allocated'), align: 'right' },
    { field: 'quantityAvailable', headerName: t('columns.available'), align: 'right' },
  ];

  return (
    <>
      <ChartCard title={t('clientSummary.stockChartTitle')} loading={isLoading} empty={!isLoading && summary.length === 0} height={320}>
        <DashboardBarChart
          layout="horizontal"
          categories={summary.map((s) => s.clientName)}
          series={[{ label: t('columns.onHand'), data: summary.map((s) => s.quantityOnHand), color: theme.palette.info.main }]}
          height={320}
        />
      </ChartCard>
      <Box sx={{ mt: 2 }}>
        <DataTable columns={columns} rows={withId(summary, (r) => r.clientName)} loading={isLoading} searchable exportFileName="client-stock-summary" />
      </Box>
    </>
  );
}

function ActivitySummaryTab() {
  const { t } = useTranslation('reports');
  const theme = useTheme();

  const { data: grns = [], isLoading: loadingGrn } = useQuery({
    queryKey: ['reports', 'grn-register'],
    queryFn: async () => (await apiClient.get<GrnRegisterRow[]>('/reports/grn-register')).data,
  });
  const { data: dispatches = [], isLoading: loadingDispatch } = useQuery({
    queryKey: ['reports', 'dispatch-order-register'],
    queryFn: async () => (await apiClient.get<DispatchOrderRegisterRow[]>('/reports/dispatch-order-register')).data,
  });

  const summary = useMemo(() => {
    const byClient = new Map<string, ClientActivitySummaryRow & { grnIds: Set<string>; dispatchIds: Set<string> }>();
    const get = (name: string) =>
      byClient.get(name) ?? { clientName: name, grnCount: 0, acceptedQty: 0, dispatchCount: 0, issuedQty: 0, grnIds: new Set<string>(), dispatchIds: new Set<string>() };

    for (const r of grns) {
      const key = r.clientName ?? UNASSIGNED;
      const entry = get(key);
      entry.grnIds.add(r.grnId);
      entry.acceptedQty += r.acceptedQty;
      byClient.set(key, entry);
    }
    for (const r of dispatches) {
      const key = r.clientName ?? UNASSIGNED;
      const entry = get(key);
      entry.dispatchIds.add(r.dispatchOrderId);
      entry.issuedQty += r.issuedQty;
      byClient.set(key, entry);
    }

    return Array.from(byClient.values())
      .map((e) => ({ ...e, grnCount: e.grnIds.size, dispatchCount: e.dispatchIds.size }))
      .sort((a, b) => b.grnCount + b.dispatchCount - (a.grnCount + a.dispatchCount));
  }, [grns, dispatches]);

  const columns: DataTableColumn<ClientActivitySummaryRow>[] = [
    { field: 'clientName', headerName: t('columns.client') },
    { field: 'grnCount', headerName: t('clientSummary.grnCount'), align: 'right' },
    { field: 'acceptedQty', headerName: t('clientSummary.acceptedQty'), align: 'right' },
    { field: 'dispatchCount', headerName: t('clientSummary.dispatchCount'), align: 'right' },
    { field: 'issuedQty', headerName: t('clientSummary.issuedQty'), align: 'right' },
  ];

  const isLoading = loadingGrn || loadingDispatch;

  return (
    <>
      <ChartCard title={t('clientSummary.activityChartTitle')} loading={isLoading} empty={!isLoading && summary.length === 0} height={320}>
        <DashboardBarChart
          layout="horizontal"
          categories={summary.map((s) => s.clientName)}
          series={[
            { label: t('clientSummary.grnCount'), data: summary.map((s) => s.grnCount), color: theme.palette.primary.main },
            { label: t('clientSummary.dispatchCount'), data: summary.map((s) => s.dispatchCount), color: theme.palette.success.main },
          ]}
          height={320}
        />
      </ChartCard>
      <Box sx={{ mt: 2 }}>
        <DataTable columns={columns} rows={withId(summary, (r) => r.clientName)} loading={isLoading} searchable exportFileName="client-activity-summary" />
      </Box>
    </>
  );
}

function BillingSummaryTab() {
  const { t } = useTranslation('reports');
  const theme = useTheme();

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: async () => (await apiClient.get<InvoiceDto[]>('/billing/invoices')).data,
  });

  const summary = useMemo(() => {
    const byClient = new Map<string, ClientBillingSummaryRow>();
    for (const inv of invoices) {
      const key = inv.clientName;
      const entry = byClient.get(key) ?? { clientName: key, invoiceCount: 0, totalInvoiced: 0, paidAmount: 0, unpaidAmount: 0 };
      entry.invoiceCount += 1;
      entry.totalInvoiced += inv.totalAmount;
      if (inv.status === 'Paid') entry.paidAmount += inv.totalAmount;
      if (inv.status === 'Finalized' || inv.status === 'Sent') entry.unpaidAmount += inv.totalAmount;
      byClient.set(key, entry);
    }
    return Array.from(byClient.values()).sort((a, b) => b.totalInvoiced - a.totalInvoiced);
  }, [invoices]);

  const columns: DataTableColumn<ClientBillingSummaryRow>[] = [
    { field: 'clientName', headerName: t('columns.client') },
    { field: 'invoiceCount', headerName: t('clientSummary.invoiceCount'), align: 'right' },
    { field: 'totalInvoiced', headerName: t('clientSummary.totalInvoiced'), align: 'right', render: (r) => `₹${r.totalInvoiced.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
    { field: 'paidAmount', headerName: t('clientSummary.paidAmount'), align: 'right', render: (r) => `₹${r.paidAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
    { field: 'unpaidAmount', headerName: t('clientSummary.unpaidAmount'), align: 'right', render: (r) => `₹${r.unpaidAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}` },
  ];

  return (
    <>
      <ChartCard title={t('clientSummary.billingChartTitle')} loading={isLoading} empty={!isLoading && summary.length === 0} height={320}>
        <DashboardBarChart
          layout="horizontal"
          categories={summary.map((s) => s.clientName)}
          series={[{ label: t('clientSummary.totalInvoiced'), data: summary.map((s) => s.totalInvoiced), color: theme.palette.warning.main }]}
          height={320}
        />
      </ChartCard>
      <Box sx={{ mt: 2 }}>
        <DataTable columns={columns} rows={withId(summary, (r) => r.clientName)} loading={isLoading} searchable exportFileName="client-billing-summary" />
      </Box>
    </>
  );
}

export default function ClientSummaryReportsPage() {
  const { t } = useTranslation('reports');
  const { data: processConfiguration } = useProcessConfiguration();
  const billingEnabled = Boolean(processConfiguration?.billingEnabled);
  const [tab, setTab] = useState(0);

  // Billing tab only makes sense while Billing itself is enabled — Stock/Activity stay available
  // as long as 3PL is on (this whole page is nav-gated on that), independent of Billing.
  const tabs = [
    { key: 'clientStock', icon: <Inventory2Icon fontSize="small" /> },
    { key: 'clientActivity', icon: <MoveToInboxIcon fontSize="small" /> },
    ...(billingEnabled ? [{ key: 'clientBilling', icon: <ReceiptLongIcon fontSize="small" /> }] : []),
  ];
  const activeTab = Math.min(tab, tabs.length - 1);

  return (
    <>
      <PageHeader title={t('modules.clientSummary.title')} subtitle={t('modules.clientSummary.subtitle')} />

      <Tabs value={activeTab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {tabs.map((tabDef) => (
          <Tab key={tabDef.key} label={t(`clientSummary.tabs.${tabDef.key}`)} />
        ))}
      </Tabs>

      <Box hidden={activeTab !== 0}>
        <StockSummaryTab />
      </Box>
      <Box hidden={activeTab !== 1}>
        <ActivitySummaryTab />
      </Box>
      {billingEnabled && (
        <Box hidden={activeTab !== 2}>
          <BillingSummaryTab />
        </Box>
      )}
    </>
  );
}
